import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type Pad, type User } from '@/lib/api';
import { MAX_MEMBERS } from '@shared/protocol.js';
import {
  Alert, Modal, initials, relativeTime, useToast,
} from './kit';
import {
  IconPlus, IconUsers, IconLogout, IconCopy, IconTrash, IconLock, IconGear,
} from './icons';
import Brand from './Brand';
import AccountModal from './AccountModal';
import UpgradeModal from './UpgradeModal';

interface Props {
  user: User;
  onEnterPad: (pad: Pad, ticket: string, role: string) => void;
  onSignOut: () => void;
  onUserChange: (user: User) => void;
  /** Code pulled from a /#/join/ABC123 link. */
  pendingCode?: string | null;
  onPendingCodeConsumed?: () => void;
}

export default function Lobby({
  user, onEnterPad, onSignOut, onUserChange, pendingCode, onPendingCodeConsumed,
}: Props) {
  const [pads, setPads] = useState<Pad[] | null>(null);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(!!pendingCode);
  const [showAccount, setShowAccount] = useState(false);
  /** Non-null while the upgrade screen is open; the string says what prompted it. */
  const [paywall, setPaywall] = useState<string | null>(null);
  const toast = useToast();

  /**
   * Anything the server refuses on plan grounds comes back as 402. Turning
   * that into the upgrade screen in one place means every limit — now and
   * later — explains itself without each call site having to know.
   */
  const handle = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 402) {
      setPaywall(err.message);
      return;
    }
    toast(err instanceof ApiError ? err.message : fallback, 'error');
  };

  const refresh = useCallback(async () => {
    try {
      const res = await api.library();
      setPads(res.pads);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your sketchpads.');
      setPads([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function openPad(pad: Pad) {
    try {
      const res = await api.openPad(pad.id);
      onEnterPad(res.pad, res.ticket, res.role);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not open that sketchpad.', 'error');
    }
  }

  async function copyPad(pad: Pad) {
    try {
      const res = await api.copyPad(pad.id, `${pad.title} (my copy)`);
      toast('Copied into a new sketchpad you host.');
      await refresh();
      onEnterPad(res.pad, res.ticket, 'host');
    } catch (err) {
      handle(err, 'Could not copy that sketchpad.');
    }
  }

  async function removePad(pad: Pad) {
    const message = pad.isOwner
      ? `Delete "${pad.title}" for everyone? This cannot be undone.`
      : `Remove "${pad.title}" from your library? The host keeps their copy.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(message)) return;
    try {
      const res = await api.deletePad(pad.id);
      toast(res.deleted ? 'Sketchpad deleted.' : 'Removed from your library.');
      refresh();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not remove that sketchpad.', 'error');
    }
  }

  const hosted = pads?.filter((p) => p.isOwner) ?? [];
  const joined = pads?.filter((p) => !p.isOwner) ?? [];

  return (
    <div className="screen" style={{ alignItems: 'start' }}>
      <div className="card card--wide">
        <div className="row row--between row--head" style={{ marginBottom: 18 }}>
          <span className="row" style={{ gap: 8, minWidth: 0 }}>
            <Brand size="md" />
            {user.plan === 'pro' ? <span className="pill-tag pill-tag--pro">Pro</span> : null}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button
              type="button"
              className="chip"
              onClick={() => setShowAccount(true)}
              title="Account and preferences"
            >
              <span className="chip__dot" style={{ background: user.avatarColor }}>
                {initials(user.username)}
              </span>
              <span className="chip__name">{user.username}</span>
            </button>
            {/* Same destination as the chip beside it, so it is the first
                thing to go when there is no room. */}
            <button type="button" className="pill-btn pill-btn--sm pill-btn--dup" onClick={() => setShowAccount(true)} aria-label="Account settings">
              <IconGear size={20} />
            </button>
            <button type="button" className="pill-btn pill-btn--sm" onClick={onSignOut} aria-label="Sign out">
              <IconLogout size={20} />
            </button>
          </div>
        </div>

        {error ? <Alert tone="error">{error}</Alert> : null}
        {!user.emailVerified ? (
          <Alert tone="info">
            <div className="row row--between row--wrap" style={{ gap: 8 }}>
              <span>Confirm your email so you can recover your account if you forget your password.</span>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => api.resendVerification()
                  .then(() => toast('Confirmation email sent.'))
                  .catch(() => toast('Could not send that right now.', 'error'))}
              >
                Resend
              </button>
            </div>
          </Alert>
        ) : null}

        <div className="row row--wrap row--actions" style={{ gap: 12 }}>
          <button type="button" className="btn btn--accent" onClick={() => setShowCreate(true)}>
            <IconPlus size={18} />
            New sketchpad
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setShowJoin(true)}>
            <IconUsers size={18} />
            Join with a code
          </button>
        </div>

        {user.plan !== 'pro' ? (
          <button type="button" className="upsell" onClick={() => setPaywall('')}>
            <span className="upsell__body">
              <span className="upsell__title">Ink Share Pro</span>
              <span className="upsell__sub">
                Draw with 5 people at once, keep 200 sketchpads, add photos, replay
                a drawing and more.
              </span>
            </span>
            <span className="upsell__cta">See plans</span>
          </button>
        ) : null}

        <div className="divider" />

        {pads === null ? (
          <div className="center muted" style={{ padding: 40 }}>
            <span className="spinner" style={{ margin: '0 auto 10px' }} />
            <div>Loading your sketchpads…</div>
          </div>
        ) : pads.length === 0 ? (
          <div className="center muted" style={{ padding: '40px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✍️</div>
            <div style={{ fontWeight: 650, color: 'var(--ink)' }}>No sketchpads yet</div>
            <div className="tiny" style={{ marginTop: 4 }}>
              Start one and share the 6-character code with up to {user.limits.drawers - 1}
              {user.limits.drawers - 1 === 1 ? ' friend' : ' friends'}.
            </div>
          </div>
        ) : (
          <>
            <PadSection
              title="Your sketchpads"
              pads={hosted}
              onOpen={openPad}
              onCopy={copyPad}
              onRemove={removePad}
            />
            {joined.length ? (
              <PadSection
                title="Drawn with friends"
                subtitle="Sketchpads you joined. Make a copy to keep drawing on one in a room of your own."
                pads={joined}
                onOpen={openPad}
                onCopy={copyPad}
                onRemove={removePad}
              />
            ) : null}
          </>
        )}
      </div>

      {showCreate ? (
        <CreatePadModal
          user={user}
          onClose={() => setShowCreate(false)}
          onPaywall={(message) => { setShowCreate(false); setPaywall(message); }}
          onCreated={(pad, ticket) => { setShowCreate(false); onEnterPad(pad, ticket, 'host'); }}
        />
      ) : null}

      {showJoin ? (
        <JoinPadModal
          initialCode={pendingCode ?? ''}
          onClose={() => { setShowJoin(false); onPendingCodeConsumed?.(); }}
          onJoined={(pad, ticket, role) => {
            setShowJoin(false);
            onPendingCodeConsumed?.();
            onEnterPad(pad, ticket, role);
          }}
        />
      ) : null}

      {paywall !== null ? (
        <UpgradeModal user={user} reason={paywall} onClose={() => setPaywall(null)} />
      ) : null}

      {showAccount ? (
        <AccountModal
          user={user}
          onClose={() => setShowAccount(false)}
          onUpgrade={() => { setShowAccount(false); setPaywall(''); }}
          onUserChange={onUserChange}
          onSignOut={onSignOut}
        />
      ) : null}
    </div>
  );
}

function PadSection({
  title, subtitle, pads, onOpen, onCopy, onRemove,
}: {
  title: string;
  subtitle?: string;
  pads: Pad[];
  onOpen: (pad: Pad) => void;
  onCopy: (pad: Pad) => void;
  onRemove: (pad: Pad) => void;
}) {
  if (!pads.length) return null;
  return (
    <section style={{ marginBottom: 24 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{title}</h3>
      {subtitle ? <p className="tiny muted" style={{ margin: '0 0 12px' }}>{subtitle}</p> : <div style={{ height: 10 }} />}
      <div className="pad-grid">
        {pads.map((pad) => (
          <div key={pad.id} className="pad-card">
            <button
              type="button"
              className="pad-card__thumb"
              onClick={() => onOpen(pad)}
              aria-label={`Open ${pad.title}`}
            >
              {pad.thumbnail
                ? <img src={pad.thumbnail} alt="" />
                : <span className="muted tiny">Empty</span>}
            </button>
            <div className="pad-card__body">
              <div className="row row--between" style={{ gap: 6 }}>
                <div className="pad-card__title grow">{pad.title}</div>
                {pad.hasPassword ? <IconLock size={14} /> : null}
              </div>
              <div className="pad-card__meta">
                {pad.isOwner
                  ? <span className="badge badge--host">Host</span>
                  : <span className="badge">{pad.ownerName}</span>}
                <span style={{ marginLeft: 6 }}>{relativeTime(pad.updatedAt)}</span>
              </div>
              <div className="row" style={{ marginTop: 10, gap: 6 }}>
                <button type="button" className="btn btn--sm grow" onClick={() => onOpen(pad)}>Open</button>
                <button
                  type="button"
                  className="pill-btn pill-btn--sm"
                  onClick={() => onCopy(pad)}
                  title="Copy into a new sketchpad you host"
                  aria-label="Copy into a new sketchpad"
                >
                  <IconCopy size={18} />
                </button>
                <button
                  type="button"
                  className="pill-btn pill-btn--sm"
                  onClick={() => onRemove(pad)}
                  title={pad.isOwner ? 'Delete' : 'Remove from my library'}
                  aria-label={pad.isOwner ? 'Delete sketchpad' : 'Remove from library'}
                >
                  <IconTrash size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CreatePadModal({
  user, onClose, onCreated, onPaywall,
}: {
  user: User;
  onClose: () => void;
  onCreated: (pad: Pad, ticket: string) => void;
  onPaywall: (message: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    api.createPad({ title: title.trim() || 'Untitled sketchpad', password: password || null })
      .then((res) => onCreated(res.pad, res.ticket))
      .catch((err) => {
        // A plan limit is not an error to apologise for — it is an offer.
        if (err instanceof ApiError && err.status === 402) onPaywall(err.message);
        else setError(err instanceof ApiError ? err.message : 'Could not create that sketchpad.');
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal title="New sketchpad" onClose={onClose}>
      <form onSubmit={submit} id="create-pad">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div className="field">
          <label htmlFor="np-title">Name</label>
          <input
            id="np-title"
            className="input"
            placeholder="Untitled sketchpad"
            maxLength={60}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="np-pw">
            Password (optional)
            {!user.limits.padPasswords ? <span className="locked" style={{ marginLeft: 8 }}><IconLock size={12} /> Pro</span> : null}
          </label>
          <input
            id="np-pw"
            className="input"
            type="text"
            autoComplete="off"
            placeholder={user.limits.padPasswords ? 'Leave blank for no password' : 'Available on Ink Share Pro'}
            value={password}
            disabled={!user.limits.padPasswords}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="tiny muted" style={{ marginTop: 6 }}>
            Anyone with the join code can draw with you. Add a password to lock it down further.
          </div>
        </div>
        <p className="tiny muted">
          You will get a 6-character code to share. Up to {user.limits.drawers} people can draw at once
          {user.limits.drawers < MAX_MEMBERS ? ' on your plan.' : '.'}
        </p>
      </form>
      <div className="modal__foot" style={{ padding: 0, border: 0, marginTop: 8 }}>
        <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button type="submit" form="create-pad" className="btn btn--accent" disabled={busy}>
          {busy ? <span className="spinner" /> : 'Create and open'}
        </button>
      </div>
    </Modal>
  );
}

function JoinPadModal({
  initialCode, onClose, onJoined,
}: {
  initialCode: string;
  onClose: () => void;
  onJoined: (pad: Pad, ticket: string, role: string) => void;
}) {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    api.joinPad({ code: code.trim().toUpperCase(), password })
      .then((res) => onJoined(res.pad, res.ticket, res.role))
      .catch((err) => {
        if (err instanceof ApiError && err.code === 'bad_password') {
          setNeedsPassword(true);
          setError(password ? 'That password is not right.' : 'This sketchpad needs a password.');
        } else {
          setError(err instanceof ApiError ? err.message : 'Could not join that sketchpad.');
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal title="Join a sketchpad" onClose={onClose}>
      <form onSubmit={submit} id="join-pad">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div className="field">
          <label htmlFor="jp-code">Join code</label>
          <input
            id="jp-code"
            className="input input--code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
            maxLength={6}
            autoFocus
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            placeholder="ABC123"
          />
        </div>
        {needsPassword ? (
          <div className="field">
            <label htmlFor="jp-pw">Password</label>
            <input
              id="jp-pw"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
        ) : null}
      </form>
      <div className="modal__foot" style={{ padding: 0, border: 0, marginTop: 8 }}>
        <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button type="submit" form="join-pad" className="btn btn--accent" disabled={busy || code.length !== 6}>
          {busy ? <span className="spinner" /> : 'Join'}
        </button>
      </div>
    </Modal>
  );
}
